import React from 'react';
import { ClipboardCheck, PackageCheck, Scissors, Flame, AlertTriangle, Box, Truck, ArrowRightLeft, Search, Repeat, Factory, Split, Palette, ClipboardList, PackagePlus, FileBarChart, ShieldCheck } from 'lucide-react';
import GenericSectionDashboard from '../GenericSectionDashboard';
import { moduleReportTiles } from '@/lib/sectionModules';
import FlowSummaryStrip from './FlowSummaryStrip';
import { JoStuckBanner, STAGE_SETS } from '@/components/processingJo/JoStuckBanner';

const InhouseDashboard = () => {
  const customTiles = [
    {
      id: 'processing-inward',
      title: 'Processing Inward',
      description: 'Receive goods from Processing — verify quantity against sent',
      icon: PackageCheck,
      iconBg: 'bg-indigo-500',
      route: '/sections/inhouse/processing-inward',
      testId: 'processing-inward-tile',
      priority: true
    },
    {
      id: 're-folding-entry',
      title: 'Re-Folding Entry',
      description: 'Mark already-minted bundles (from any section) for Re-Folding — skips Processing Inward',
      icon: Repeat,
      iconBg: 'bg-rose-500',
      route: '/sections/inhouse/re-folding-entry',
      testId: 're-folding-entry-tile',
      priority: true
    },
    {
      id: 'color-assortment-entry',
      title: 'Color Assortment Entry',
      description: 'Scan rework bundles straight to Assortment Packing — skips Folding, Pressing, Segregation & Damage',
      icon: Palette,
      iconBg: 'bg-fuchsia-500',
      route: '/sections/inhouse/color-assortment-entry',
      testId: 'color-assortment-entry-tile',
      priority: true
    },
    {
      id: 'folding',
      title: 'Cutting & Folding',
      description: 'Record cutting & folding operations for a staff pair',
      icon: Scissors,
      iconBg: 'bg-orange-500',
      route: '/sections/inhouse/folding',
      testId: 'folding-tile',
      priority: true
    },
    {
      id: 'segregation',
      title: 'Segregation',
      description: 'Print USN stickers, then sort every piece into Good (→ Assortment Packing) & Damage (→ Damage Checking) — multi-staff',
      icon: Split,
      iconBg: 'bg-lime-600',
      route: '/sections/inhouse/segregation',
      testId: 'segregation-tile',
      priority: true
    },
    {
      id: 'damage-checking',
      title: 'Damage Checking',
      description: 'Inspect folded pieces — clear, design transfer, or return to supplier',
      icon: AlertTriangle,
      iconBg: 'bg-amber-500',
      route: '/sections/inhouse/damage-checking',
      testId: 'damage-checking-tile',
      priority: true
    },
    {
      id: 'pressing-history',
      title: 'Pressing History',
      description: 'View past pressing sessions — pressing is now automatic at Assortment Packing',
      icon: Flame,
      iconBg: 'bg-rose-500',
      route: '/sections/inhouse/pressing/history',
      testId: 'pressing-history-tile',
      requires_any: ['pressing'],
      priority: true
    },
    {
      id: 'assortment-packing',
      title: 'Assortment Packing',
      description: 'Scan USNs into bundles — pieces are auto-marked pressed on scan (multi-staff)',
      icon: Box,
      iconBg: 'bg-cyan-500',
      route: '/sections/inhouse/assortment-packing',
      testId: 'assortment-packing-tile',
      priority: true
    },
    {
      id: 'pjo-return-checks',
      title: 'Job Order Return Checks',
      description: "Approve returned job-order bundles that don't match what was sent (pieces, sort no, colour)",
      icon: ShieldCheck,
      iconBg: 'bg-sky-700',
      route: '/sections/inhouse/return-checks',
      testId: 'pjo-return-checks-tile',
      priority: true
    },
    {
      id: 'design-transfer',
      title: 'Design Transfer',
      description: 'Scan PACKED bundles and remap all USNs to a destination design',
      icon: ArrowRightLeft,
      iconBg: 'bg-fuchsia-500',
      route: '/sections/inhouse/design-transfer',
      testId: 'design-transfer-tile',
      priority: true
    },
    {
      id: 'create-job-order',
      title: 'Create Job Order',
      description: 'Raise a Job Order — pick a jobber, a target item & scan READY Job-Worker bundles',
      icon: PackagePlus,
      iconBg: 'bg-indigo-600',
      route: '/sections/inhouse/create-job-order',
      testId: 'create-job-order-tile',
      priority: true
    },
    {
      id: 'job-worker-return',
      title: 'Job Worker Return',
      description: 'Pick a Job Order — paste Good & Damage USNs to tally the return (Good → Assortment, Damage → Damage Op)',
      icon: Factory,
      iconBg: 'bg-indigo-600',
      route: '/sections/inhouse/job-worker-return',
      testId: 'job-worker-return-tile',
      priority: true
    },
    {
      id: 'jobber-report',
      title: 'Jobber Report',
      description: 'Sent vs Good / Damage, balance & status across all Job Orders',
      icon: FileBarChart,
      iconBg: 'bg-slate-700',
      route: '/sections/inhouse/jobber-report',
      testId: 'jobber-report-tile',
      priority: true
    },
    {
      id: 'direct-ship',
      title: 'Direct Ship',
      description: 'Ship bundles to sites or customers — create box & A4 slip',
      icon: Truck,
      iconBg: 'bg-emerald-500',
      route: '/sections/inhouse/direct-ship',
      testId: 'direct-ship-tile',
      priority: true
    },
    {
      id: 'qty-confirmation',
      title: 'Inward Qty Confirmation',
      description: 'Verify received bundle quantities (Mandatory)',
      icon: ClipboardCheck,
      iconBg: 'bg-red-500',
      route: '/sections/inhouse/qty-confirmation',
      testId: 'qty-confirmation-tile',
      priority: true
    },
    {
      id: 'rts-documents',
      title: 'Supplier Return Docs',
      description: 'Saved Return-to-Supplier documents — create boxes & print supplier slips',
      icon: Truck,
      iconBg: 'bg-red-600',
      route: '/sections/inhouse/return-to-supplier/documents',
      testId: 'rts-documents-tile'
    },
    {
      id: 'usn-report',
      title: 'USN Status Report',
      description: 'Search by USN, Bundle, Design — view Folded / Damage / Pressed / Shipped status',
      icon: Search,
      iconBg: 'bg-slate-600',
      route: '/sections/inhouse/usn-report',
      testId: 'usn-report-tile'
    },
    {
      id: 'operations-report',
      title: 'Operations Report',
      description: 'Date/time log across all operations — Sort No, FG Barcode, Design, Qty (IST) with CSV export',
      icon: ClipboardList,
      iconBg: 'bg-slate-700',
      route: '/sections/inhouse/operations-report',
      testId: 'operations-report-tile'
    }
  ];

  return (
    <GenericSectionDashboard
      sectionName="Inhouse Section"
      sectionId="inhouse"
      sectionKey="Inhouse"
      description="In-house Processing Operations"
      backRoute="/"
      customTiles={[...customTiles, ...moduleReportTiles('inhouse', { it: false, docs: false })]}
      headerExtras={<div className="space-y-4">
        <JoStuckBanner stages={STAGE_SETS.inhouse} testId="inhouse-jo-stuck-banner" />
        <FlowSummaryStrip days={7} />
      </div>}
    />
  );
};

export default InhouseDashboard;
